import { UnparseObject } from 'papaparse';

// Spreadsheet applications (Excel, Google Sheets, LibreOffice) interpret any
// cell whose text begins with one of these characters as a formula. A crafted
// value such as `=HYPERLINK("http://evil","click me")` placed in exported user
// data (names, custom fields, …) would then execute when an admin opens the
// file — this is "CSV / formula injection". Quoting the field (which PapaParse
// already does) prevents delimiter/newline breakage but does NOT stop this:
// the quotes are stripped on open and the cell text is still a formula.
const CSV_FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Neutralize a single cell value against CSV / spreadsheet formula injection.
 *
 * If the value starts with a formula-trigger character it is prefixed with an
 * apostrophe, which forces the spreadsheet to render it as literal text. Plain
 * numbers (e.g. `-5`, `+42`) are left untouched so legitimate numeric data is
 * not silently turned into text; `+1-555-…`-style phone strings are not valid
 * numbers and so are (correctly) escaped as text.
 */
export const escapeCsvFormulaInjection = (value: string): string => {
  if (!value || !CSV_FORMULA_PREFIXES.includes(value[0])) {
    return value;
  }
  return Number.isFinite(Number(value)) ? value : `'${value}`;
};

const escapeCell = <T>(cell: T): T =>
  (typeof cell === 'string'
    ? escapeCsvFormulaInjection(cell)
    : cell) as T;

/**
 * Apply {@link escapeCsvFormulaInjection} to every string cell (and header),
 * for each of the input shapes PapaParse's `unparse` accepts. Non-string cells
 * (numbers, booleans) can never start with a trigger character once rendered,
 * so they are passed through unchanged.
 */
export const sanitizeCsvInput = (
  data: Array<Object> | Array<Array<any>> | UnparseObject<any>
): Array<Object> | Array<Array<any>> | UnparseObject<any> => {
  if (Array.isArray(data)) {
    return data.map((row: any) =>
      Array.isArray(row)
        ? row.map(escapeCell)
        : Object.fromEntries(
            Object.entries(row).map(([key, value]) => [key, escapeCell(value)])
          )
    );
  }
  return {
    ...data,
    fields: data.fields.map((field: string) => escapeCell(field)),
    data: data.data.map((row: any) =>
      Array.isArray(row) ? row.map(escapeCell) : escapeCell(row)
    )
  };
};
